export const textPanning = () => {
  
  let cells = [];
  let isTouchDevice = false;
  
  const init = () => {
    // Detect touch device
    isTouchDevice = checkIfTouchDevice();
    
    // Re-query cells to handle dynamic content changes (sorting, filtering)
    updateCells();
    bind();
    
    // Re-initialize when window is resized
    window.addEventListener('resize', debounce(handleResize, 250));
  };

  const checkIfTouchDevice = () => {
    return (
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints > 0) ||
      (window.DocumentTouch && document instanceof DocumentTouch)
    );
  };

  const respectsReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const updateCells = () => {
    // Clear previous event listeners and classes
    cells.forEach(cell => {
      cell.removeEventListener('mouseenter', handleMouseEnter);
      cell.removeEventListener('mouseleave', handleMouseLeave);
      cell.classList.remove('has-truncated-text', 'is-panning', 'is-auto-panning');
    });
    
    // Get fresh cell list
    cells = Array.from(document.querySelectorAll(".c-projects-list__item__cell"));
  };

  const bind = () => {
    // Don't animate if user prefers reduced motion
    if (respectsReducedMotion()) {
      cells.forEach((cell) => {
        if (isTextTruncated(cell)) {
          cell.classList.add('has-truncated-text');
        } else {
          cell.classList.remove('has-truncated-text');
        }
      });
      return;
    }

    cells.forEach((cell) => {
      // Check if text is truncated
      if (isTextTruncated(cell)) {
        cell.classList.add('has-truncated-text');
        
        if (isTouchDevice) {
          // On touch devices, start animation automatically
          startAutoPanning(cell);
        } else {
          // On desktop, use hover events
          cell.addEventListener('mouseenter', handleMouseEnter);
          cell.addEventListener('mouseleave', handleMouseLeave);
        }
      } else {
        cell.classList.remove('has-truncated-text');
        // Stop any existing animation
        if (isTouchDevice) {
          stopAutoPanning(cell);
        }
      }
    });
  };

  const isTextTruncated = (element) => {
    // Check if element is visible first
    if (element.offsetParent === null || element.clientWidth === 0) {
      return false;
    }
    return element.scrollWidth > element.clientWidth;
  };

  const handleMouseEnter = (event) => {
    const cell = event.currentTarget;
    const textWidth = cell.scrollWidth;
    const containerWidth = cell.clientWidth;
    const overflowAmount = textWidth - containerWidth;
    
    // Only animate if there's actually overflow
    if (overflowAmount <= 0) return;
    
    // Calculate animation duration based on overflow amount
    // Slower for longer text, but reasonable bounds
    const animationDuration = Math.min(Math.max(1.5, overflowAmount / 40), 6);
    
    // Set data-text attribute for the ::before pseudo-element
    cell.setAttribute('data-text', cell.textContent);
    
    // Set CSS custom properties for the animation
    cell.style.setProperty('--overflow-amount', `-${overflowAmount}px`);
    cell.style.setProperty('--animation-duration', `${animationDuration}s`);
    
    // Add animation class
    cell.classList.add('is-panning');
  };

  const handleMouseLeave = (event) => {
    const cell = event.currentTarget;
    
    // Remove animation class
    cell.classList.remove('is-panning');
  };

  const startAutoPanning = (cell) => {
    const textWidth = cell.scrollWidth;
    const containerWidth = cell.clientWidth;
    const overflowAmount = textWidth - containerWidth;
    
    // Only animate if there's actually overflow
    if (overflowAmount <= 0) return;
    
    // Calculate animation duration for mobile (3x slower than desktop)
    const baseDuration = Math.min(Math.max(1.2, overflowAmount / 50), 5);
    const animationDuration = baseDuration * 3; // 3 times slower
    
    // Set data-text attribute for the ::before pseudo-element
    cell.setAttribute('data-text', cell.textContent);
    
    // Set CSS custom properties for the animation
    cell.style.setProperty('--overflow-amount', `-${overflowAmount}px`);
    cell.style.setProperty('--animation-duration', `${animationDuration}s`);
    
    // Add mobile-specific class for continuous animation
    cell.classList.add('is-auto-panning');
  };

  const stopAutoPanning = (cell) => {
    // Remove auto-panning class
    cell.classList.remove('is-auto-panning');
  };

  const handleResize = () => {
    updateCells();
    bind();
  };

  // Debounce utility function
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Public method to reinitialize (useful for dynamic content)
  const reinit = () => {
    updateCells();
    bind();
  };

  init();
  
  // Return public API
  return {
    reinit
  };
};
